import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { Panel } from "../../ds";
import { MiniSparkline } from "../../components/MiniSparkline";
import { Markdown } from "../../components/Markdown";
import { fmtDateTime } from "../../lib/time";
import { AlertTriangle } from "lucide-react";

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-md border border-[color:var(--line)] bg-[color:var(--panel-2)] p-3">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[color:var(--muted)]">{label}</div>
      <div className="font-display text-2xl tabular-nums mt-0.5">{value}</div>
      {hint && <div className="font-mono text-[10px] text-[color:var(--muted)] mt-0.5">{hint}</div>}
    </div>
  );
}

/**
 * SEO tab contents.
 *
 * `section` (optional) narrows the render to a single sub-view used by the
 * SEO hub pages. When omitted (ProjectDetail → Tabs), the full stack of
 * panels renders exactly as before — no breaking change.
 *
 * Sections: "overview" | "reports" | "keywords" | "worklog"
 */
export function ProjectSeoTab({ slug, section }) {
  const { data } = useQuery({
    queryKey: ["project-seo", slug],
    queryFn: async () => (await api.get(`/projects/${slug}/seo`)).data,
  });
  const snap = data?.snapshot;
  if (!snap) {
    return <Panel label="SEO" testId="seo-tab-empty">
      <p className="text-sm text-[color:var(--muted)]">No data yet. SEO Lead has not pushed a snapshot.</p>
    </Panel>;
  }

  const show = (s) => !section || section === s;
  const gsc = snap.gsc || {};
  const ga4 = snap.ga4 || {};
  const src = snap.source_status || {};
  const sourceErr = src.gsc === "error" || src.ga4 === "error";

  return (
    <div className="space-y-4" data-testid="seo-tab">
      {show("overview") && sourceErr && (
        <div className="flex items-start gap-2 border border-amber-500/30 bg-amber-500/5 rounded-md p-3"
             data-testid="seo-source-error">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-xs">
            Data source error: GSC={src.gsc}, GA4={src.ga4}. {src.note}
          </p>
        </div>
      )}

      {show("overview") && (
        <Panel label={`GOOGLE SEARCH CONSOLE${snap.month ? ` · ${snap.month}` : ""}`} testId="seo-gsc">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Clicks" value={gsc.clicks || 0} />
            <Stat label="Impressions" value={gsc.impressions || 0} />
            <Stat label="CTR" value={`${((gsc.ctr || 0) * 100).toFixed(2)}%`} />
            <Stat label="Avg position" value={(gsc.position || 0).toFixed(1)} />
          </div>
          {(gsc.daily || []).length > 1 && (
            <div className="mt-4">
              <div className="font-mono text-[10px] text-[color:var(--muted)] mb-1">28-day clicks trend</div>
              <MiniSparkline data={(gsc.daily || []).map((d) => d.clicks || 0)} height={48} data-testid="seo-clicks-spark" />
            </div>
          )}
        </Panel>
      )}

      {show("overview") && (
        <Panel label="GA4" testId="seo-ga4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Stat label="Users" value={ga4.users || 0} />
            <Stat label="Sessions" value={ga4.sessions || 0} />
            <Stat label="Engaged" value={ga4.engaged_sessions || 0} />
          </div>
          {(ga4.daily || []).length > 1 && (
            <div className="mt-4">
              <div className="font-mono text-[10px] text-[color:var(--muted)] mb-1">28-day sessions trend</div>
              <MiniSparkline data={(ga4.daily || []).map((d) => d.sessions || 0)} height={48} data-testid="seo-sessions-spark" />
            </div>
          )}
        </Panel>
      )}

      {show("keywords") && (snap.keywords || []).length > 0 && (
        <Panel label="KEYWORDS" testId="seo-keywords">
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[480px]">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase text-[color:var(--muted)] border-b border-[color:var(--line)]">
                  <th className="py-2 pr-3">Keyword</th>
                  <th className="py-2 pr-3 text-right">Pos</th>
                  <th className="py-2 pr-3 text-right">Clicks</th>
                  <th className="py-2 pr-3 text-right">Impr</th>
                  <th className="py-2 text-right">Target</th>
                </tr>
              </thead>
              <tbody>
                {snap.keywords.map((k, i) => (
                  <tr key={i} className="border-b border-[color:var(--line)] last:border-b-0">
                    <td className="py-1.5 pr-3">{k.keyword}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{k.position?.toFixed(1) ?? "—"}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{k.clicks}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{k.impressions}</td>
                    <td className="py-1.5 text-right font-mono tabular-nums text-[color:var(--muted)]">{k.target ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {show("keywords") && (gsc.top_queries || []).length > 0 && (
        <Panel label="TOP QUERIES" testId="seo-top-queries">
          <ul className="text-xs space-y-1">
            {gsc.top_queries.slice(0, 10).map((q, i) => (
              <li key={i} className="flex justify-between border-b border-[color:var(--line)] last:border-b-0 py-1">
                <span>{q.query || q.keyword || q.text}</span>
                <span className="font-mono text-[color:var(--muted)]">{q.clicks ?? ""}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {show("reports") && snap.report?.markdown && (
        <Panel label={`MONTHLY REPORT${snap.report.month ? ` · ${snap.report.month}` : ""}`} testId="seo-report">
          <Markdown>{snap.report.markdown}</Markdown>
        </Panel>
      )}
      {show("reports") && !snap.report?.markdown && (
        <Panel label="MONTHLY REPORT" testId="seo-report-empty">
          <p className="text-sm text-[color:var(--muted)]">No monthly report yet.</p>
        </Panel>
      )}

      {show("worklog") && (snap.worklog || []).length > 0 && (
        <Panel label="WORKLOG" testId="seo-worklog">
          <ul className="text-xs space-y-1">
            {snap.worklog.slice().reverse().slice(0, 50).map((w, i) => (
              <li key={i} className="flex gap-2 text-[color:var(--muted)]">
                <span className="font-mono tabular-nums">{fmtDateTime(w.at)}</span>
                <span className="text-[color:var(--fg)]">{w.agent}</span>
                <span>— {w.action}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {show("worklog") && (snap.worklog || []).length === 0 && (
        <Panel label="WORKLOG" testId="seo-worklog-empty">
          <p className="text-sm text-[color:var(--muted)]">No worklog entries.</p>
        </Panel>
      )}
    </div>
  );
}

/**
 * Marketing tab contents.
 *
 * `section` (optional): "queue" | "plans" | "gallery". Omitted = full stack.
 */
export function ProjectMarketingTab({ slug, section }) {
  const { data } = useQuery({
    queryKey: ["project-marketing", slug],
    queryFn: async () => (await api.get(`/projects/${slug}/marketing`)).data,
  });
  const [channelFilter, setChannelFilter] = React.useState("all");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const snap = data?.snapshot;
  if (!snap) {
    return <Panel label="MARKETING" testId="marketing-tab-empty">
      <p className="text-sm text-[color:var(--muted)]">No data yet. Marketing Lead has not pushed a snapshot.</p>
    </Panel>;
  }

  const show = (s) => !section || section === s;
  const queue = snap.queue || [];
  const planVersions = snap.plan_versions || [];
  const gallery = snap.gallery || [];
  const hold = snap.hold || {};

  const channels = ["all", ...new Set(queue.map((q) => q.channel))];
  const statuses = ["all", "waiting_licence_hold", "scheduled", "published", "failed", "cancelled"];
  const filteredQueue = queue.filter((q) =>
    (channelFilter === "all" || q.channel === channelFilter) &&
    (statusFilter === "all" || q.status === statusFilter)
  );
  const galleryByChannel = gallery.reduce((acc, g) => {
    (acc[g.channel] ||= []).push(g); return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="marketing-tab">
      {show("queue") && hold.active && (
        <div className="flex items-start gap-2 border border-amber-500/40 bg-amber-500/10 rounded-md p-3"
             data-testid="marketing-hold">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-xs">
            <b>Licence hold</b> — {hold.reason || "no reason provided."}
          </p>
        </div>
      )}

      {show("queue") && (
        <Panel label="PUBLISHING QUEUE" testId="marketing-queue">
          <div className="flex gap-2 mb-3 flex-wrap">
            <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)}
                    data-testid="marketing-filter-channel"
                    className="font-mono text-xs bg-[color:var(--panel-2)] border border-[color:var(--line)] rounded px-2 py-1">
              {channels.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="marketing-filter-status"
                    className="font-mono text-xs bg-[color:var(--panel-2)] border border-[color:var(--line)] rounded px-2 py-1">
              {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[720px]">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase text-[color:var(--muted)] border-b border-[color:var(--line)]">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Time (UAE)</th>
                  <th className="py-2 pr-3">Channel</th>
                  <th className="py-2 pr-3">Format</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2">v</th>
                </tr>
              </thead>
              <tbody>
                {filteredQueue.length === 0 && (
                  <tr><td colSpan={6} className="py-3 text-[color:var(--muted)]">Empty.</td></tr>
                )}
                {filteredQueue.map((q) => (
                  <tr key={q.id} data-testid={`q-row-${q.id}`} className="border-b border-[color:var(--line)] last:border-b-0">
                    <td className="py-1.5 pr-3 font-mono">{q.date || "—"}</td>
                    <td className="py-1.5 pr-3 font-mono tabular-nums">{q.time || "—"}</td>
                    <td className="py-1.5 pr-3">{q.channel}</td>
                    <td className="py-1.5 pr-3">{q.format}</td>
                    <td className="py-1.5 pr-3">
                      <span className={
                        "inline-block rounded px-1.5 py-0.5 font-mono text-[10px] uppercase " +
                        (q.status === "published" ? "bg-emerald-500/15 text-emerald-500"
                          : q.status === "failed" ? "bg-rose-500/15 text-rose-500"
                          : q.status === "waiting_licence_hold" ? "bg-amber-500/15 text-amber-500"
                          : q.status === "cancelled" ? "bg-zinc-500/15 text-zinc-500"
                          : "bg-[color:var(--cobalt-glow)] text-[color:var(--cobalt)]")
                      }>{q.status}</span>
                    </td>
                    <td className="py-1.5 font-mono tabular-nums text-[color:var(--muted)]">v{q.plan_version ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {show("plans") && planVersions.length > 0 && (
        <Panel label="PLAN VERSIONS" testId="marketing-plans">
          <ul className="space-y-1 text-xs">
            {planVersions.map((p) => (
              <li key={`${p.task_id}-${p.version}`} className="flex items-center gap-3 py-1 border-b border-[color:var(--line)] last:border-b-0">
                <span className="font-mono tabular-nums w-12">v{p.version}</span>
                <span className="flex-1">{p.changes_summary || p.note || "—"}</span>
                <span className="font-mono text-[color:var(--muted)]">{p.post_count} post</span>
                {p.approved && <span className="font-mono text-[10px] uppercase text-emerald-500">approved</span>}
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {show("plans") && planVersions.length === 0 && (
        <Panel label="PLAN VERSIONS" testId="marketing-plans-empty">
          <p className="text-sm text-[color:var(--muted)]">No plan versions yet.</p>
        </Panel>
      )}

      {show("gallery") && gallery.length > 0 && (
        <Panel label="GALLERY" testId="marketing-gallery">
          <div className="space-y-4">
            {Object.entries(galleryByChannel).map(([ch, items]) => (
              <div key={ch}>
                <div className="font-mono text-[10px] uppercase tracking-wider text-[color:var(--muted)] mb-2">{ch}</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {items.map((g, i) => (
                    <a key={`${g.post_id}-${i}`} href={g.url} target="_blank" rel="noreferrer"
                       className="group block rounded border border-[color:var(--line)] overflow-hidden hover:border-[color:var(--cobalt)]"
                       data-testid={`gallery-${g.post_id}`}>
                      {g.kind === "webm" || g.kind === "mp4"
                        ? <video src={g.url} className="w-full aspect-square object-cover" muted />
                        : <img src={g.url} alt={g.post_id} className="w-full aspect-square object-cover" />}
                      <div className="px-2 py-1 font-mono text-[10px] text-[color:var(--muted)] bg-[color:var(--panel-2)]">
                        {g.format}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}
      {show("gallery") && gallery.length === 0 && (
        <Panel label="GALLERY" testId="marketing-gallery-empty">
          <p className="text-sm text-[color:var(--muted)]">No media uploaded.</p>
        </Panel>
      )}
    </div>
  );
}
